import CheckIcon from '@mui/icons-material/Check';
import ReplayIcon from '@mui/icons-material/Replay';
import { IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useUpdateComment } from '@/api/mutations/useCommentMutations';
import { useCommentableTextAtoms } from '@/comments';
import { Tooltip } from '@/components/ui/tooltip';
import { useTextAtomSave } from '@/data/editorDataLayer';
import { CommentableStatus } from '@/enums';
import { useRequiredAppSelector } from '@/hooks';
import type { IComment } from '@/types';
import { deleteCommentInTextAtomContent, invalidateAfterCommentUpdate, textableTypeToTextableModel } from './helpers';

interface CommentItemHeaderProps {
    id: IComment['id'];
    textAtomId: IComment['commentable']['id'];
    status: IComment['status'];
}

export const CommentItemHeaderActionsResolve = (props: CommentItemHeaderProps) => {
    const { id, textAtomId, status } = props;
    const { t } = useTranslation();
    const project_id = useRequiredAppSelector('projectId');
    const { textAtoms: commentableTextAtoms } = useCommentableTextAtoms();
    const { mutateAsync: updateCommentAsync } = useUpdateComment();
    const textAtom = commentableTextAtoms?.[textAtomId];

    const saveAtom = useTextAtomSave(
        {
            textableId: textAtom?.textable_id ?? 0,
            textableModel: textableTypeToTextableModel(textAtom?.textable_type),
            textAtomTypeId: textAtom?.text_atom_type_id ?? 1,
            successCallback: () => invalidateAfterCommentUpdate(null)(),
        },
        textAtom,
    );

    const handleResolve = async () => {
        await updateCommentAsync({
            project_id,
            comment_id: id,
            data: {
                status: status === CommentableStatus.active ? CommentableStatus.resolved : CommentableStatus.active,
            },
        });
        saveAtom(deleteCommentInTextAtomContent((textAtom?.content ?? '') as string, id));
    };

    return (
        <IconButton onClick={handleResolve} sx={{ padding: 0 }}>
            {status === CommentableStatus.active ? (
                <Tooltip title={t('comment.accept')} placement="bottom-start">
                    <CheckIcon color="success" fontSize="small" />
                </Tooltip>
            ) : (
                <ReplayIcon color="primary" />
            )}
        </IconButton>
    );
};
